import { AppShell, Box, Center, SimpleGrid, Stack, Tabs } from "@mantine/core";
import {
  IconHierarchy3,
  IconListCheck,
  IconMapPins,
  IconMessageChatbot
} from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router";
import { ROUTES } from "../../../routes";
import type { SiteName } from "../../../utils/enums";
import { Brand } from "../../Layout/Brand";
import {
  NAVBAR_SCROLLBAR_COLOR,
  NAVBAR_SHADOW
} from "../../Menu/Menu.constants";
import type { MenuItemData } from "../../Menu/Menu.types";
import { Group } from "../../Menu/Menu.types";
import { MenuItem } from "../../Menu/MenuItem/MenuItem";
import { ModuleSwitchItem } from "../../Menu/MenuItem/ModuleSwitchItem";

declare const SITE_NAME: SiteName;

const ADMIN_DASHBOARD_URLS = {
  CENTRAL_TASK_LIST: "/admin-dashboard/aufgaben",
  PROPERTY: "/admin-dashboard/liegenschaftsuebersicht",
  MSB_PROCESS: "/admin-dashboard/msb-prozess/",
  LOCATION_OVERVIEW: "/admin-dashboard/msb-prozess/lokationsuebersicht",
  METERING_SERVICE_PROVIDER:
    "/admin-dashboard/msb-prozess/messstellenbetreiber",
  OPTI_AGENT_FEEDBACK: "/admin-dashboard/opti-agent-feedback"
} as const;

const MENU_ITEMS: Array<MenuItemData> = [
  {
    url: ADMIN_DASHBOARD_URLS.CENTRAL_TASK_LIST,
    group: Group.ModuleChoice,
    icon: IconListCheck,
    label: "Zentrale Aufgabenliste",
    showGroups: [Group.TopLevel, Group.ModuleChoice]
  },
  {
    url: ADMIN_DASHBOARD_URLS.PROPERTY,
    group: Group.ModuleChoice,
    icon: IconMapPins,
    label: "Liegenschaften",
    showGroups: [Group.TopLevel, Group.ModuleChoice]
  },
  {
    url: ADMIN_DASHBOARD_URLS.MSB_PROCESS,
    group: Group.ModuleChoice,
    icon: IconHierarchy3,
    label: "MSB Prozess",
    showGroups: [Group.TopLevel, Group.ModuleChoice]
  },
  {
    url: ADMIN_DASHBOARD_URLS.OPTI_AGENT_FEEDBACK,
    group: Group.ModuleChoice,
    icon: IconMessageChatbot,
    label: "opti.Agent\nFeedback",
    showGroups: [Group.TopLevel, Group.ModuleChoice]
  }
];

interface AdminDashboardLayoutProps {
  children: React.ReactNode;
}

export function AdminDashboardLayout({ children }: AdminDashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();

  function renderTabs() {
    switch (removeTrailingSlash(location.pathname)) {
      case ADMIN_DASHBOARD_URLS.CENTRAL_TASK_LIST:
        return (
          <Tabs.Tab
            component="a"
            value={ADMIN_DASHBOARD_URLS.CENTRAL_TASK_LIST}
          >
            Aufgaben
          </Tabs.Tab>
        );
      case ADMIN_DASHBOARD_URLS.PROPERTY:
        return (
          <Tabs.Tab component="a" value={ADMIN_DASHBOARD_URLS.PROPERTY}>
            Liegenschaftsübersicht
          </Tabs.Tab>
        );
      case ADMIN_DASHBOARD_URLS.MSB_PROCESS:
      case ADMIN_DASHBOARD_URLS.METERING_SERVICE_PROVIDER:
      case ADMIN_DASHBOARD_URLS.LOCATION_OVERVIEW:
        return (
          <>
            <Tabs.Tab
              component="a"
              value={ADMIN_DASHBOARD_URLS.LOCATION_OVERVIEW}
            >
              Lokationsübersicht
            </Tabs.Tab>
            <Tabs.Tab
              component="a"
              value={ADMIN_DASHBOARD_URLS.METERING_SERVICE_PROVIDER}
            >
              Messstellenbetreiber
            </Tabs.Tab>
          </>
        );
      case ADMIN_DASHBOARD_URLS.OPTI_AGENT_FEEDBACK:
        return (
          <Tabs.Tab
            component="a"
            value={ADMIN_DASHBOARD_URLS.OPTI_AGENT_FEEDBACK}
          >
            opti.Agent Feedback
          </Tabs.Tab>
        );
      default:
      // no default
    }
  }

  return (
    <AppShell
      className="Layout"
      navbar={{ width: 150, breakpoint: 0 }}
      withBorder={false}
    >
      <AppShell.Navbar bg="secondary.0" style={{ boxShadow: NAVBAR_SHADOW }}>
        <div
          style={{
            overflowX: "hidden",
            scrollbarColor: `${NAVBAR_SCROLLBAR_COLOR} transparent`,
            scrollbarWidth: "thin"
          }}
        >
          <Center bg="secondary.0" mih={80}>
            <Brand
              isAdminDashboardPage
              siteName={SITE_NAME}
              url={ROUTES.productSelectPage}
            />
          </Center>

          {MENU_ITEMS.map((menuItem) => (
            <MenuItem
              active={
                menuItem.url
                  ? location.pathname.includes(
                      removeTrailingSlash(menuItem.url)
                    )
                  : false
              }
              key={menuItem.url}
              {...menuItem}
            />
          ))}
          <ModuleSwitchItem isAdminDashboardPage />
        </div>
      </AppShell.Navbar>
      <AppShell.Main component={SimpleGrid}>
        <Stack gap={0}>
          <Box bg="white" p="sm" pb={0}>
            <Tabs
              defaultValue={removeTrailingSlash(location.pathname)}
              onChange={(newTab) => navigate(`${newTab}`)}
            >
              <Tabs.List>{renderTabs()}</Tabs.List>
            </Tabs>
          </Box>
          <Box flex={1} p={"sm"}>
            {children}
          </Box>
        </Stack>
      </AppShell.Main>
    </AppShell>
  );
}

function removeTrailingSlash(url: string) {
  return url.replace(/\/$/, "");
}
